import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Toolbar — the ONE filter-bar row. Every direct child (search input, select,
 * toggle chip, button) is pinned to a single control height so a row can never
 * mix 26/30/34px controls again. The `end` slot is pinned out of the wrapping
 * flow, so wrapping cannot reorder it.
 */

export interface ToolbarProps {
  children: ReactNode;
  /** Right-hand slot pinned OUT of the flex flow, so wrapping cannot reorder it. */
  end?: ReactNode;
  /** md = 32px (--size-control-md, the toolbar default) · sm = 28px. */
  size?: 'sm' | 'md';
  className?: string;
}

const pinBySize = {
  sm: '[&>*:not([data-toolbar-static])]:h-[var(--size-control-sm)]',
  md: '[&>*:not([data-toolbar-static])]:h-[var(--size-control-md)]',
} as const;

function ToolbarRoot({ children, end, size = 'md', className }: ToolbarProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('flex min-w-0 flex-1 flex-wrap items-center gap-2', pinBySize[size])}>
        {children}
      </div>
      {end !== undefined && (
        <div className={cn('ml-auto flex shrink-0 items-center gap-2', pinBySize[size])}>{end}</div>
      )}
    </div>
  );
}

/** Joined segmented cluster: one border, one radius, -ml-px seams. */
export function ToolbarGroup({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center',
        '[&>*]:h-full [&>*]:rounded-none [&>*+*]:-ml-px',
        '[&>*:first-child]:rounded-l-[var(--radius-control)] [&>*:last-child]:rounded-r-[var(--radius-control)]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** 1px vertical rule between toolbar clusters. */
export function ToolbarDivider({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-toolbar-static
      aria-hidden="true"
      className={cn('my-1 w-px self-stretch bg-border', className)}
      {...props}
    />
  );
}

/** The one legal inline toolbar label. */
export function ToolbarLabel({ className, children, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-toolbar-static
      className={cn('inline-flex items-center whitespace-nowrap text-sm text-ink-faint', className)}
      {...props}
    >
      {children}
    </span>
  );
}

export interface ToolbarChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Toggle state. Selected = green-900 fill; unselected = card bg + border. */
  selected?: boolean;
  icon?: LucideIcon;
  count?: number;
}

/**
 * ToolbarChip — the ONE toggle/filter chip. Replaces the ad-hoc filter ovals.
 * control-md height, radius-control; segmented = "filters what you are looking
 * at" (navigation underlines live in Tabs).
 */
export function ToolbarChip({
  selected = false,
  icon: Icon,
  count,
  className,
  children,
  type,
  ...props
}: ToolbarChipProps) {
  return (
    <button
      type={type ?? 'button'}
      aria-pressed={selected}
      className={cn(
        't-label inline-flex h-[var(--size-control-md)] items-center gap-1 whitespace-nowrap rounded-[var(--radius-control)] border px-3 transition-colors',
        selected
          ? 'border-green-900 bg-green-900 text-white hover:bg-green-800'
          : 'border-border bg-surface-card text-ink-soft hover:border-border-strong hover:text-ink',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {Icon && <Icon size={16} className="shrink-0" aria-hidden="true" />}
      {children}
      {count !== undefined && (
        <span
          className={cn(
            'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-2xs font-semibold leading-none',
            selected ? 'bg-green-800 text-green-100' : 'bg-cream-dark text-ink-soft',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

const Toolbar = Object.assign(ToolbarRoot, {
  Group: ToolbarGroup,
  Divider: ToolbarDivider,
  Label: ToolbarLabel,
  Chip: ToolbarChip,
});

export default Toolbar;
